import { useEffect, useMemo } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { ModernHeader } from './ModernHeader';
import { ModernFooter } from './ModernFooter';
import A11yToolbar from '@/components/accessibility/A11yToolbar';
import { api } from '@/lib/api';
import { cn } from '@/lib/utils';
import { parseInstitutionSubdomainFromHostname } from '@/lib/institution-public-subdomain';
import { useTranslation } from '@/hooks/useTranslation';

const SKIP_LINK_LABELS: Record<string, string> = {
  fr: 'Aller au contenu principal',
  en: 'Skip to main content',
  es: 'Ir al contenido principal',
  it: 'Vai al contenuto principale',
  de: 'Zum Hauptinhalt springen',
  pt: 'Saltar para o conteúdo principal',
  ar: 'انتقل إلى المحتوى الرئيسي',
  ru: 'Перейти к основному содержимому',
  uk: 'Перейти до основного вмісту',
  lb: 'Zum Haaptinhalt sprangen',
  tr: 'Ana içeriğe geç',
};

export function PublicLayout() {
  const location = useLocation();
  const { language } = useTranslation();
  const skipLabel = SKIP_LINK_LABELS[language] ?? SKIP_LINK_LABELS.en;
  const institutionSubdomain = useMemo(
    () => (typeof window !== 'undefined' ? parseInstitutionSubdomainFromHostname(window.location.hostname) : null),
    [],
  );

  useEffect(() => {
    const path = `${location.pathname}${location.search || ''}`;
    void api
      .post('/public/telemetry/public-page-visit', {
        path,
        url: window.location.href,
        referrer: document.referrer || undefined,
      })
      .catch(() => undefined);
  }, [location.pathname, location.search]);

  if (institutionSubdomain && location.pathname !== '/') {
    return <Navigate to="/" replace />;
  }

  const hideMarketingChrome = Boolean(institutionSubdomain);

  return (
    <div className={cn('flex min-h-screen flex-col', !hideMarketingChrome && 'public-marketing public-readable')}>
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:left-4 focus:top-4 focus:z-[100] focus:rounded-lg focus:bg-[#001B3D] focus:px-4 focus:py-2 focus:text-sm focus:font-semibold focus:text-white focus:shadow-lg focus:outline focus:outline-2 focus:outline-offset-2 focus:outline-white"
      >
        {skipLabel}
      </a>
      {!hideMarketingChrome && <ModernHeader />}
      <div className="flex min-h-0 flex-1 flex-col">
        <Outlet />
      </div>
      {!hideMarketingChrome && <ModernFooter />}
      <A11yToolbar fabPosition="left" panelPosition="left" />
    </div>
  );
}
